import { getSDK } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';

import { isFromHiddenAgentChat } from '@/features/home/proactive-agent-visibility';
import { useHiddenProactiveAgentChatIds } from '@/features/settings/proactive-agent.query';
import { useTabs } from '@/features/tabs';
import { cn } from '@/lib/utils';

import { useCanvasActionCardPlacements } from '../canvas-action-card-placement-context';

import { ActionCardItem } from './ActionCardItem';

const CARD_ARRIVAL_GRACE_MS = 10_000;

// Breathing room against surrounding prose. Between back-to-back cards only the
// later card's top margin applies, so the gap does not depend on margin collapsing.
// The `br` variants cover markdown putting consecutive card links in one paragraph
// split by a line break
const INLINE_CARD_FRAME_CLASS = [
  'lb-inline-action-card block my-3',
  '[&+br]:hidden',
  '[&:has(+.lb-inline-action-card)]:mb-0',
  '[&:has(+br+.lb-inline-action-card)]:mb-0',
].join(' ');

const SOURCE_CHAT_TAG_CLASS =
  'shrink-0 whitespace-nowrap rounded-md bg-surface-hover px-2 py-0.5 text-[0.6875rem] leading-4 text-foreground-muted';

const CardPlaceholder = () => (
  <span className="not-prose block w-full max-w-2xl" role="status">
    <span className="block overflow-hidden rounded-2xl border border-border-strong bg-surface shadow-sm">
      <span className="flex items-start gap-3 px-4 pb-2.5 pt-3.5">
        <span className="size-8 shrink-0 animate-pulse rounded-md bg-surface-active opacity-30" />
        <span className="min-w-0 flex-1">
          <span className="block h-3 w-32 animate-pulse rounded-md bg-surface-active opacity-30" />
          <span className="mt-2 block h-3 w-20 animate-pulse rounded-md bg-surface-active opacity-30" />
        </span>
      </span>
      <span className="block px-4 pb-4 pt-1">
        <span className="block h-3 w-full animate-pulse rounded-md bg-surface-active opacity-30" />
        <span className="mt-2 block h-3 w-2/3 animate-pulse rounded-md bg-surface-active opacity-30" />
      </span>
    </span>
  </span>
);

/** A link can arrive before the card it points at, so an unknown id waits a while. */
export const InlineActionCard = observer(
  ({ chatId, localId }: { chatId: string; localId: number }) => {
    const [waitedTooLong, setWaitedTooLong] = useState(false);
    const { canvasOnlyLocalIds, isLoading: isCanvasPlacementLoading } =
      useCanvasActionCardPlacements();
    const { openTab } = useTabs();
    const hiddenAgentChatIds = useHiddenProactiveAgentChatIds();
    const { actionCards, reports, routines, threads } = getSDK().stores;
    const card = actionCards.getByLocalId(localId);
    const sourceThread = card?.chatId ? threads.get(card.chatId) : undefined;
    const heldReport =
      card?.origin === 'routine' && card.chatId
        ? reports.getByChatId(card.chatId)
        : undefined;
    const sourceRoutineId =
      card?.reportTemplateId ?? heldReport?.reportTemplateId;
    const sourceReportId = card?.reportId ?? heldReport?.referenceId;
    const sourceRun =
      card?.origin === 'routine' &&
      sourceRoutineId !== undefined &&
      sourceReportId !== undefined
        ? { routineId: sourceRoutineId, reportId: sourceReportId }
        : undefined;
    const sourceRoutine = sourceRun
      ? routines.getByReferenceId(sourceRun.routineId)
      : undefined;
    const sourceTitle = (
      sourceRun ? sourceRoutine?.title : sourceThread?.title
    )?.trim();
    const fromHiddenAgent =
      !!card &&
      isFromHiddenAgentChat(card, hiddenAgentChatIds, {
        reportByChatId: (reportChatId) => reports.getByChatId(reportChatId),
        routineByReferenceId: (referenceId) =>
          routines.getByReferenceId(referenceId),
      });
    // Sources the user may not open never sync here, so link only to one that did.
    // A hidden proactive agent's chats and routines stay unnamed.
    const hasOpenableSource =
      !fromHiddenAgent &&
      (card?.origin === 'routine'
        ? Boolean(sourceRoutine)
        : Boolean(sourceThread));
    // An agent can link any of the user's cards, so a card it did not make here always says so, even when its
    // source can't be opened
    const fromAnotherChat = card?.chatId !== chatId;
    // A routine card is judged by its routine, so only other cards need their chat loaded
    const sourceChatId = card?.origin === 'routine' ? undefined : card?.chatId;

    useEffect(() => {
      if (sourceChatId)
        void threads.ensureLoaded(sourceChatId).catch(() => undefined);
    }, [sourceChatId, threads]);

    useEffect(() => {
      setWaitedTooLong(false);
      const timer = setTimeout(
        () => setWaitedTooLong(true),
        CARD_ARRIVAL_GRACE_MS
      );
      return () => clearTimeout(timer);
    }, [chatId, localId]);

    if (isCanvasPlacementLoading || canvasOnlyLocalIds.has(localId))
      return null;

    if (card) {
      return (
        <span
          className={cn(
            INLINE_CARD_FRAME_CLASS,
            card.actionType === 'ask_user' && 'w-full max-w-[85%]'
          )}
        >
          <ActionCardItem
            card={card}
            sourceChatTag={
              !fromAnotherChat ? undefined : hasOpenableSource ? (
                <button
                  className={cn(
                    SOURCE_CHAT_TAG_CLASS,
                    'hover:bg-surface-active hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring'
                  )}
                  type="button"
                  title={sourceTitle || 'Open source chat'}
                  aria-label={
                    sourceTitle
                      ? `From another chat: ${sourceTitle}`
                      : 'From another chat'
                  }
                  onClick={() =>
                    sourceRun
                      ? openTab({
                          path: `/routines/${sourceRun.routineId}`,
                          search: {
                            view: 'canvas',
                            run: sourceRun.reportId,
                          },
                        })
                      : openTab({ path: `/chats/${card.chatId}` })
                  }
                >
                  From another chat
                </button>
              ) : (
                <span className={SOURCE_CHAT_TAG_CLASS}>From another chat</span>
              )
            }
          />
        </span>
      );
    }
    if (waitedTooLong) return null;

    return (
      <span className={INLINE_CARD_FRAME_CLASS}>
        <CardPlaceholder />
      </span>
    );
  }
);
